"use client";

import { logger } from "@utilities/logger";
import { useCallback, useLayoutEffect, useEffect, useRef, useState, useMemo } from "react";

// ============================================================================
// Types
// ============================================================================

export type MutationStatus = "idle" | "pending" | "success" | "error";

/**
 * Options for configuring a mutation
 */
export interface UseMutationOptions<TData = unknown, TError = Error, TVariables = void> {
  /**
   * The function that performs the mutation.
   * Receives the variables passed to mutate/mutateAsync.
   */
  mutationFn: (variables: TVariables) => Promise<TData>;

  /**
   * Called when the mutation succeeds.
   * Receives the data and variables.
   */
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;

  /**
   * Called when the mutation fails.
   * Receives the error and variables.
   */
  onError?: (error: TError, variables: TVariables) => void | Promise<void>;
}

/**
 * Internal state representation using discriminated unions
 * to ensure type-safe state transitions
 */
type MutationState<TData, TError, TVariables> =
  | { status: "idle"; data: undefined; error: null; variables: undefined }
  | { status: "pending"; data: undefined; error: null; variables: TVariables }
  | { status: "success"; data: TData; error: null; variables: TVariables }
  | { status: "error"; data: undefined; error: TError; variables: TVariables };

/**
 * The return type of the useMutation hook
 */
export interface UseMutationResult<TData = unknown, TError = Error, TVariables = void> {
  /**
   * Fire-and-forget mutation trigger.
   * Errors are caught and stored in the error state.
   */
  mutate: (variables: TVariables) => void;

  /**
   * Async mutation trigger that returns a promise.
   * Allows await and try/catch at the call site.
   */
  mutateAsync: (variables: TVariables) => Promise<TData>;

  /**
   * Reset the mutation state to idle.
   * Cancels any pending mutation (ignores its result).
   */
  reset: () => void;

  /** The data returned by the last successful mutation */
  data: TData | undefined;

  /** The error from the last failed mutation */
  error: TError | null;

  /** The variables passed to the last mutation call */
  variables: TVariables | undefined;

  /** Current status of the mutation */
  status: MutationStatus;

  /** True when status is 'idle' */
  isIdle: boolean;

  /** True when status is 'pending' */
  isPending: boolean;

  /** True when status is 'success' */
  isSuccess: boolean;

  /** True when status is 'error' */
  isError: boolean;
}

// ============================================================================
// Initial State
// ============================================================================

const INITIAL_STATE: MutationState<never, never, never> = {
  status: "idle",
  data: undefined,
  error: null,
  variables: undefined,
};

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * A lightweight mutation hook inspired by React Query, using only React primitives.
 *
 * Features:
 * - Race condition handling (latest mutation wins)
 * - Unmount safety (no state updates after unmount)
 * - Both fire-and-forget (mutate) and async (mutateAsync) APIs
 * - Reset functionality that cancels pending mutations
 *
 * @example
 * ```tsx
 * const { mutate, isPending, error } = useMutation({
 *   mutationFn: async (data: FormData) => {
 *     const response = await fetch('/api/submit', {
 *       method: 'POST',
 *       body: data,
 *     });
 *     if (!response.ok) throw new Error('Failed to submit');
 *     return response.json();
 *   },
 *   onSuccess: (data) => {
 *     logger.info('Success!', data);
 *   },
 *   onError: (error) => {
 *     console.error('Failed:', error);
 *   },
 * });
 *
 * // Fire and forget
 * mutate(formData);
 *
 * // Or await the result
 * try {
 *   const result = await mutateAsync(formData);
 * } catch (error) {
 *   // Handle error
 * }
 * ```
 */
export function useMutation<TData = unknown, TError = Error, TVariables = void>(
  options: UseMutationOptions<TData, TError, TVariables>,
): UseMutationResult<TData, TError, TVariables> {
  // Track whether the component is still mounted to avoid state updates after unmount
  const isMountedRef = useRef(true);

  // Track the current mutation ID to handle race conditions
  // When a new mutation starts or reset is called, this ID is incremented
  // Only the mutation matching the current ID will update state
  const mutationIdRef = useRef(0);

  // Store the latest options in a ref to avoid stale closures in callbacks
  // This ensures onSuccess/onError always have access to the latest values
  const optionsRef = useRef(options);

  // Update the options ref on every render
  useLayoutEffect(() => {
    optionsRef.current = options;
  });

  // Set mounted flag to false on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Main state using discriminated union for type safety
  const [state, setState] = useState<MutationState<TData, TError, TVariables>>(
    () => INITIAL_STATE as MutationState<TData, TError, TVariables>,
  );

  /**
   * Core mutation execution logic.
   * Handles the full lifecycle: mutationFn -> onSuccess/onError
   */
  const executeMutation = useCallback(async (variables: TVariables): Promise<TData> => {
    // Increment mutation ID to invalidate any previous pending mutations
    const currentMutationId = ++mutationIdRef.current;

    // Helper to check if this mutation is still the latest
    const isLatestMutation = () => mutationIdRef.current === currentMutationId;

    // Helper to safely update state (checks mounted + latest mutation)
    const safeSetState = (newState: MutationState<TData, TError, TVariables>) => {
      if (isMountedRef.current && isLatestMutation()) {
        setState(newState);
      }
    };

    // Set pending state
    safeSetState({
      status: "pending",
      data: undefined,
      error: null,
      variables,
    });

    try {
      // Execute the mutation function
      const data = await optionsRef.current.mutationFn(variables);

      // Only update state if this is still the latest mutation and component is mounted
      if (!isLatestMutation()) {
        // This mutation was superseded, but we still need to return/throw for mutateAsync
        return data;
      }

      safeSetState({
        status: "success",
        data,
        error: null,
        variables,
      });

      // Execute onSuccess callback
      if (optionsRef.current.onSuccess) {
        try {
          await optionsRef.current.onSuccess(data, variables);
        } catch (onSuccessError) {
          // Errors in onSuccess should not affect the mutation result
          logger.error(
            onSuccessError instanceof Error ? onSuccessError : null,
            "useMutation: onSuccess callback threw an error",
          );
        }
      }

      return data;
    } catch (error) {
      const typedError = error as TError;

      // Only update state if this is still the latest mutation and component is mounted
      if (isLatestMutation()) {
        safeSetState({
          status: "error",
          data: undefined,
          error: typedError,
          variables,
        });
      }

      // Execute onError callback
      if (optionsRef.current.onError) {
        try {
          await optionsRef.current.onError(typedError, variables);
        } catch (onErrorError) {
          // Errors in onError should not affect the mutation result
          logger.error(
            onErrorError instanceof Error ? onErrorError : null,
            "useMutation: onError callback threw an error",
          );
        }
      }

      // Re-throw for mutateAsync callers
      throw typedError;
    }
  }, []);

  /**
   * Fire-and-forget mutation trigger.
   * Errors are caught and stored in state, not thrown.
   */
  const mutate = useCallback(
    (variables: TVariables) => {
      executeMutation(variables).catch(() => {
        // Error is already handled and stored in state
        // Swallow to prevent unhandled rejection
      });
    },
    [executeMutation],
  );

  /**
   * Async mutation trigger.
   * Returns a promise that resolves with the data or rejects with the error.
   */
  const mutateAsync = useCallback(
    (variables: TVariables): Promise<TData> => {
      return executeMutation(variables);
    },
    [executeMutation],
  );

  /**
   * Reset the mutation state to idle.
   * Increments the mutation ID to invalidate any pending mutations.
   */
  const reset = useCallback(() => {
    // Increment mutation ID to invalidate any pending mutations
    mutationIdRef.current++;

    setState(INITIAL_STATE as MutationState<TData, TError, TVariables>);
  }, []);

  // Derive boolean flags from status for convenience
  const result = useMemo(
    (): UseMutationResult<TData, TError, TVariables> => ({
      mutate,
      mutateAsync,
      reset,
      data: state.data,
      error: state.error,
      variables: state.variables,
      status: state.status,
      isIdle: state.status === "idle",
      isPending: state.status === "pending",
      isSuccess: state.status === "success",
      isError: state.status === "error",
    }),
    [mutate, mutateAsync, reset, state],
  );

  return result;
}
